import { Component, OnInit } from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { Router, RoutesRecognized } from '@angular/router';
import get from 'lodash-es/get';

import { AuthService, ConfigurationService, SiteSettingsService } from '@reg/shared/service';
import { IExternalLink, SiteSettings } from '@reg/shared/model';

@Component({
    selector: 'flo-header',
    animations: [
        trigger('isVisibleChanged', [
            state('true', style({ opacity: 1, transition: 'max-height' })),
            state('false', style({ opacity: 0, transition: 'max-height' })),
            transition('0 => 1', animate('250ms')),
            transition('1 => 0', animate('450ms')),
        ]),
    ],
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit {
    /**
     * Hides or shows the share icon dropdown on desktop nav
     * @type {boolean}
     */
    public showShareIcons: boolean = false;

    /**
     * Hides or shows mobile drop down
     * @type {boolean}
     */
    public showDropdown: boolean = false;

    /**
     * Used to toggle the header
     * @type {boolean}
     */
    public showHeader: boolean = false;

    /**
     * Hides or shows user's account dropdown
     * @type {boolean}
     */
    public showAccountDropdown: boolean = false;

    /**
     * Custom Nav options defined in the CMS up to the siteSettings maxHeaderLinkCount
     * @type {Array}
     */
    public customNav: Array<any> = [];

    /**
     * All Custom Nav options defined in the CMS
     * @type {Array}
     */
    public customNavAll: Array<any> = [];

    /**
     * Custom Mobile Nav Shown in Dropdown
     * @type {Array}
     */
    public mobileSubNav: Array<any> = [];

    /**
     * Custom mobile nav for account settings shown in dropdown
     * @type {Array}
     */
    public mobileAccountSettingsNav: Array<any> = [];

    public didScroll: boolean = false;
    public headerPos: number = 0;
    public navBarHeight: number = 50;
    public profilePicture: string = '';
    public sportConfigHeader: any;
    public siteSettings: SiteSettings;

    constructor(
                private router: Router,
                public authService: AuthService,
                public siteSettingsService: SiteSettingsService,
                private configService: ConfigurationService,
    ) {
        this.sportConfigHeader = configService.getSportConfig('header');
    }

    /**
     * Listens to the routing events to decide if the firstChild of the root route needs to hide the header
     */
    public ngOnInit(): void {
        this.getCustomNav();

        this.router.events.subscribe(
            // We don't have a type for this because the events don't share a common type
            (event: any) => {
                if (event instanceof RoutesRecognized) {
                    this.setShowHeader(event);
                }
            },
        );
    }

    public isFloProMember(): boolean {
        return this.authService.user && this.authService.user.isFloProMember;
    }

    public getCustomNav(): void {
        this.siteSettingsService
            .getBySiteId(this.configService.getSportConfig('siteId'))
            .subscribe((siteSettings: SiteSettings) => {
                this.siteSettings = siteSettings;
                this.buildNavSequence(siteSettings);
            });
    }

    /**
     * This returns whether the first child of the route route has the hideNav in the data
     * Data is static data associated with the route
     *   root -> AppComponent
     *      child -> Article , Auth etc
     *
     *      We know the root aka auth will always have one child route which is why this is safe.
     *
     * @param event
     * @returns {boolean}
     */
    public showNav(event: RoutesRecognized): boolean {
        return !get(event, 'state.root.firstChild.data.hideNav', false);
    }

    /**
     * Sets show header variable which controls navigation
     * @param event
     */
    public setShowHeader(event: RoutesRecognized): void {
        this.showHeader = this.showNav(event);
    }

    /**
     * Method used in template to hide / show the header
     */
    public hideHeader(): boolean {
        return !this.showHeader;
    }

    /**
     * Method used in template to hide / show the share icons on desktop
     */
    public isShareDropdownShown(): void {
        this.showShareIcons = !this.showShareIcons;
        this.showAccountDropdown = false;
    }

    /**
     * Method used in template to hide / show the mobile dropdown menu
     */
    public isDropdownShown(): void {
        this.showDropdown = !this.showDropdown;
    }

    /**
     * Method used in template to hide / show the mobile dropdown menu
     */
    public isAccountDropdownShown(): void {
        this.showAccountDropdown = !this.showAccountDropdown;
        this.showShareIcons = false;
    }

    /**
     * Method used in template to hide all dropdown menus if mouse navigates away from the nav
     */
    public hideDropdowns(): void {
        this.showShareIcons = false;
        this.showAccountDropdown = false;
    }

    /**
     * Logs the user out / deletes the jws token / clears out profile picture
     */
    public logout(): void {
        this.profilePicture = '';
        this.showDropdown = false;
        this.authService.logout();
    }

    public scrollEvent(): void {
        const yScroll: number = window.scrollY;

        if (yScroll > this.headerPos && yScroll > this.navBarHeight && this.showDropdown !== true) {
            // scrolling down - hide nav by setting didScroll to true
            this.didScroll = true;
            this.headerPos = yScroll;
        } else {
            // scrolling up - show nav by setting didScroll to false
            this.didScroll = false;
            this.headerPos = yScroll;
        }
    }

    /**
     * Builds out the nav list based on custom options from the API. 'News' is always listed.
     */
    public buildNavSequence(siteSettings: SiteSettings): void {
        this.customNav = [];

        siteSettings.customNav.forEach((name: string) => {
            const el: IExternalLink = siteSettings.externalLinks.filter(
                (x: IExternalLink) => x.name.toLowerCase() === name.toLowerCase(),
            )[0];

            if (el) {
                this.customNavAll.push({
                    name: name,
                    link: el.link,
                });
            } else {
                this.customNavAll.push({
                    name: name,
                    link: `${siteSettings.coreSiteUrl}/${this.getRouteOverride(name)}`,
                });
            }
        });

        this.customNav = this.customNavAll.slice(0, siteSettings.maxHeaderLinkCount);

        // All but the first 4 will show in the mobile dropdown
        this.buildMobileSubNav(this.customNavAll);

        // as well as using their new SiteSettings api
        this.buildMobileAccountSettingsNav(siteSettings);
    }

    public getRouteOverride(routeName: string): string {
        routeName = routeName.toLowerCase();

        if (routeName === 'news') {
            return 'articles';
        }

        return routeName;
    }

    /**
     * The first four nav items will display, and the remaining will be shown in the dropdown
     * @param nav
     * @returns {Array<any>}
     */
    public buildMobileSubNav(nav: Array<any>): Array<any> {
        this.mobileSubNav = nav.slice(4);

        return this.mobileSubNav;
    }

    /**
     * Additional site setting links normally in the footer will also be added
     * This is a new array for that separate dropdown
     */
    public buildMobileAccountSettingsNav(siteSettings: SiteSettings): void {
        if (!siteSettings) {
            return;
        }

        if (siteSettings.advertiseUrl) {
            this.mobileAccountSettingsNav.push({ name: 'Advertise', link: siteSettings.advertiseUrl });
        }
        if (siteSettings.careersUrl) {
            this.mobileAccountSettingsNav.push({ name: 'Careers', link: siteSettings.careersUrl });
        }
        if (siteSettings.contactUrl) {
            this.mobileAccountSettingsNav.push({ name: 'Contact', link: siteSettings.contactUrl });
        }
        if (siteSettings.faqUrl) {
            this.mobileAccountSettingsNav.push({ name: 'FAQ', link: siteSettings.faqUrl });
        }
        if (siteSettings.floSportsUrl) {
            this.mobileAccountSettingsNav.push({ name: 'About FloSports', link: siteSettings.floSportsUrl });
        }
        if (siteSettings.privacyUrl) {
            this.mobileAccountSettingsNav.push({ name: 'Privacy Policy', link: siteSettings.privacyUrl });
        }
        if (siteSettings.termsOfUseUrl) {
            this.mobileAccountSettingsNav.push({ name: 'Terms Of Use', link: siteSettings.termsOfUseUrl });
        }
    }

    public onUpgradeToPro(): void {
        this.isDropdownShown();
        window.location.href = this.sportConfigHeader.signUpUrl;
    }

    public getProfilePhoto(): string {
        if (this.authService.user &&
            this.authService.user.profilePicture) {
            return this.authService.user.profilePicture;
        }

        return 'https://res.cloudinary.com/diznifoln/image/upload/w_100,h_100/ntp6qsztqiio8dybdly6.png';
    }
}
